import { useCallback, useEffect, useMemo, useRef, useState, type FormEvent } from "react";
import { useNavigate } from "react-router-dom";
import {
  CheckCircle2,
  XCircle,
  Tag,
  Link2,
  Play,
  Pause,
  Undo2,
  ArrowRight,
} from "@/lib/icons";
import GameLayout from "@/games/shared/GameLayout";
import PlayerSetup from "@/games/shared/PlayerSetup";
import Scoreboard from "@/games/shared/Scoreboard";
import TurnTimer from "@/games/shared/TurnTimer";
import MatchResultDialog from "@/games/shared/MatchResultDialog";
import { LiveAnnouncer, useAnnouncer } from "@/games/shared/LiveAnnouncer";
import GameMenu from "@/games/shared/GameMenu";
import { useHotSeatMatch, type PlayerId } from "@/games/shared/useHotSeatMatch";
import { play } from "@/games/shared/sound";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Label } from "@/components/ui/label";
import {
  GAME_CATEGORIES,
  getCategory,
  difficultyStars,
  type GameCategoryKey,
} from "@/games/elementBattle/categories";
import {
  validateMove,
  categoryPoolSize,
  type GameMode,
} from "@/games/elementBattle/engine";
import MiniPeriodicTable, { type CellState } from "@/games/elementBattle/MiniPeriodicTable";
import RoundRecap, { type RecapMistake } from "@/games/elementBattle/RoundRecap";
import { elements } from "@/data/elements";

const TIMER_OPTIONS = [0, 10, 15, 30] as const;
type TimerOption = (typeof TIMER_OPTIONS)[number];
const ROUNDS_TO_WIN = 2; // best-of-3

type RoundEntry = {
  player: PlayerId;
  display: string;
  number: number;
  /** Set when accepted via fuzzy-match - used for the "Did you mean…" toast. */
  correctedFrom?: string;
};

type RoundState = {
  index: number;
  mode: GameMode;
  categoryKey?: GameCategoryKey;
  entries: RoundEntry[];
  /** Wrong attempts captured for the recap. */
  mistakes: RecapMistake[];
  /** Player who lost this round (so the OTHER scores). null while in progress. */
  loser: PlayerId | null;
  loseReason?: string;
};

type FeedbackBanner =
  | { kind: "success"; text: string }
  | { kind: "error"; text: string }
  | null;

/**
 * Element Battle - hot-seat element-naming game.
 *
 * See `.lovable/element-battle-improvements.md` for the wider roadmap. This
 * file currently implements:
 *  - Onboarding "How to play" dialog (auto-open once per device).
 *  - Pausable, color-independent turn UI with SR announcements.
 *  - Difficulty-tagged categories.
 *  - Symbol/name fuzzy-matching via the engine; per-round mistakes captured
 *    for the post-round Recap.
 *  - In-round Reset and Undo for teacher arbitration.
 *  - RoundRecap teaching screen between rounds (named/missed coverage,
 *    one-line element facts, mistake review, formative quick-check).
 */
export default function ElementBattle() {
  const navigate = useNavigate();
  const match = useHotSeatMatch(0);
  const announce = useAnnouncer();

  const [round, setRound] = useState<RoundState | null>(null);
  const [input, setInput] = useState("");
  const [feedback, setFeedback] = useState<FeedbackBanner>(null);
  const [turnSeconds, setTurnSeconds] = useState<TimerOption>(15);
  const timerEnabled = turnSeconds > 0;
  const [paused, setPaused] = useState(false);
  const [pendingMode, setPendingMode] = useState<GameMode>("category");
  const [pendingCategory, setPendingCategory] = useState<GameCategoryKey>("noble-gas");
  const inputRef = useRef<HTMLInputElement>(null);

  const lastEntry = round?.entries[round.entries.length - 1] ?? null;
  const requiredLetter =
    round?.mode === "last-letter" && lastEntry
      ? lastEntry.display
          .replace(/\s*\(.*\)$/, "")
          .replace(/[^a-zA-Z]/g, "")
          .slice(-1)
          .toUpperCase()
      : null;

  const usedNumbers = useMemo(
    () => new Set(round?.entries.map((e) => e.number) ?? []),
    [round],
  );

  // Focus the input whenever it's the active player's turn.
  useEffect(() => {
    if (round && round.loser === null && !paused) {
      inputRef.current?.focus();
    }
  }, [round, match.state.currentTurn, paused]);

  // Pre-compute pending category preview (must be above any early return).
  const previewElements = useMemo(() => {
    if (pendingMode !== "category") return [];
    const cat = getCategory(pendingCategory);
    return elements.filter((e) => cat.matches.includes(e.category));
  }, [pendingMode, pendingCategory]);
  const previewStates = useMemo(() => {
    const m = new Map<number, CellState>();
    for (const el of previewElements) m.set(el.number, "highlight");
    return m;
  }, [previewElements]);

  // -------- match lifecycle --------

  const startMatch = useCallback(
    (p1: string, p2: string) => match.start(p1, p2),
    [match],
  );

  const beginRound = useCallback(() => {
    setRound({
      index: (round?.index ?? 0) + 1,
      mode: pendingMode,
      categoryKey: pendingMode === "category" ? pendingCategory : undefined,
      entries: [],
      mistakes: [],
      loser: null,
    });
    setInput("");
    setFeedback(null);
    setPaused(false);
    announce(
      `Round ${(round?.index ?? 0) + 1} started. ${match.state.players[0].name}'s turn.`,
    );
  }, [pendingMode, pendingCategory, round, match.state.players, announce]);

  const endRound = useCallback(
    (loser: PlayerId, reason: string) => {
      setRound((r) => (r ? { ...r, loser, loseReason: reason } : r));
      const winner: PlayerId = loser === 0 ? 1 : 0;
      const newScore = match.state.stats[winner] + 1;
      match.setStat(winner, newScore);
      if (newScore >= ROUNDS_TO_WIN) {
        play("win");
        match.finish(winner);
        announce(
          `Match over. ${match.state.players[winner].name} wins ${newScore} to ${match.state.stats[loser]}.`,
        );
      } else {
        play("wrong");
        announce(
          `Round won by ${match.state.players[winner].name}. ${reason}`,
        );
      }
    },
    [match, announce],
  );

  // -------- per-turn handlers --------

  const submitMove = (e: FormEvent) => {
    e.preventDefault();
    if (!round || round.loser !== null || paused) return;

    const result = validateMove(
      input,
      { mode: round.mode, categoryKey: round.categoryKey },
      usedNumbers,
      lastEntry?.display.replace(/\s*\(.*\)$/, "") ?? null,
    );

    if (result.ok === false) {
      const explanation = explanationFor(result, round.categoryKey);
      // Capture for the recap
      setRound((r) =>
        r
          ? {
              ...r,
              mistakes: [
                ...r.mistakes,
                {
                  player: match.state.currentTurn,
                  attempt: input.trim().slice(0, 30) || "(empty)",
                  explanation,
                },
              ],
            }
          : r,
      );
      setFeedback({ kind: "error", text: result.message });
      announce(`Wrong: ${result.message}`);
      endRound(match.state.currentTurn, result.message);
      return;
    }

    const success = result;
    const correctionNote = success.correctedFrom
      ? ` (accepted from "${success.correctedFrom}")`
      : "";
    setRound((r) =>
      r
        ? {
            ...r,
            entries: [
              ...r.entries,
              {
                player: match.state.currentTurn,
                display: success.display,
                number: success.element.number,
                correctedFrom: success.correctedFrom,
              },
            ],
          }
        : r,
    );
    setFeedback({
      kind: "success",
      text: `✓ ${success.display}${correctionNote}`,
    });
    play("correct");
    setInput("");
    const next = match.state.currentTurn === 0 ? 1 : 0;
    announce(
      `Correct${correctionNote}. ${match.state.players[next].name}'s turn.`,
    );
    match.nextTurn();
  };

  const onTimerExpire = useCallback(() => {
    if (!round || round.loser !== null) return;
    const reason = `Time's up - ${match.state.players[match.state.currentTurn].name} couldn't answer in ${turnSeconds}s.`;
    setFeedback({ kind: "error", text: reason });
    setRound((r) =>
      r
        ? {
            ...r,
            mistakes: [
              ...r.mistakes,
              {
                player: match.state.currentTurn,
                attempt: "(no answer)",
                explanation: `Ran out of time after ${r.entries.filter((e) => e.player === match.state.currentTurn).length} correct.`,
              },
            ],
          }
        : r,
    );
    endRound(match.state.currentTurn, reason);
  }, [round, match, endRound, turnSeconds]);

  const undoLast = () => {
    if (!round || round.entries.length === 0 || round.loser !== null) return;
    const popped = round.entries[round.entries.length - 1];
    setRound((r) =>
      r ? { ...r, entries: r.entries.slice(0, -1) } : r,
    );
    setFeedback({
      kind: "error",
      text: `Undid last answer (${popped.display}). Turn returned to ${match.state.players[popped.player].name}.`,
    });
    // Roll the turn back to the player whose answer was undone.
    if (match.state.currentTurn !== popped.player) {
      match.nextTurn();
    }
    setInput("");
  };

  const resetMatch = () => {
    match.start(match.state.players[0].name, match.state.players[1].name);
    setRound(null);
    setFeedback(null);
    setInput("");
    setPaused(false);
  };

  const handleRematch = () => {
    match.start(match.state.players[0].name, match.state.players[1].name);
    setRound(null);
    setFeedback(null);
    setInput("");
    setPaused(false);
  };
  const handleChangePlayers = () => {
    match.reset();
    setRound(null);
    setFeedback(null);
    setInput("");
    setPaused(false);
  };
  const handleExit = () => navigate("/play");

  // -------- render branches --------

  if (match.state.status === "setup") {
    return (
      <GameLayout
        title="Element Battle"
        subtitle="Take turns naming elements. First to fail loses the round. Best of 3."
        headerActions={<GameMenu gameKey="element-battle" />}
      >
        <LiveAnnouncer />
        <PlayerSetup
          title="Set up the match"
          description="Two players, one device. Enter nicknames or leave blank to use defaults."
          onStart={startMatch}
        />
      </GameLayout>
    );
  }

  const betweenRounds = round === null || round.loser !== null;
  const matchOver = match.state.status === "finished";
  const activeCategory = round?.categoryKey ? getCategory(round.categoryKey) : null;
  const currentPlayerName = match.state.players[match.state.currentTurn].name;

  return (
    <GameLayout
      title="Element Battle"
      subtitle="Best of 3 rounds. Round ends when a player can't answer."
      headerActions={
        <GameMenu
          gameKey="element-battle"
          onReset={resetMatch}
          resetLabel="Reset match"
        />
      }
    >
      <LiveAnnouncer />

      <div className="mb-5">
        <Scoreboard state={match.state} statLabel="Rounds" />
      </div>

      {/* Recap (round just ended, match still going) */}
      {betweenRounds && !matchOver && round && round.loser !== null && (
        <RoundRecap
          roundIndex={round.index}
          categoryKey={round.categoryKey}
          winnerName={match.state.players[round.loser === 0 ? 1 : 0].name}
          loseReason={round.loseReason}
          namedNumbers={usedNumbers}
          mistakes={round.mistakes}
          playerNames={[match.state.players[0].name, match.state.players[1].name]}
          continueLabel={`Set up round ${round.index + 1}`}
          onContinue={() => {
            // Scroll to the setup card below - no state change needed; the
            // recap stays visible above for reference.
            document
              .getElementById("eb-next-round-setup")
              ?.scrollIntoView({ behavior: "smooth", block: "start" });
          }}
        />
      )}

      {betweenRounds && !matchOver && (
        <div
          id="eb-next-round-setup"
          className="rounded-xl border border-border bg-card p-5 mb-5"
        >
          <h3 className="text-base font-semibold text-foreground mb-4">
            Round {(round?.index ?? 0) + 1} setup
          </h3>

          {/* Mode picker */}
          <div className="mb-5">
            <span className="block text-[10px] font-semibold uppercase tracking-wider text-muted-foreground mb-2">
              Mode
            </span>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              {(["category", "last-letter"] as GameMode[]).map((m) => {
                const active = pendingMode === m;
                const Icon = m === "category" ? Tag : Link2;
                return (
                  <button
                    key={m}
                    type="button"
                    onClick={() => setPendingMode(m)}
                    aria-pressed={active}
                    className={`text-left p-3 rounded-md border transition-colors flex items-start gap-2.5 ${
                      active
                        ? "border-primary bg-primary/5"
                        : "border-border bg-background hover:border-primary/40"
                    }`}
                  >
                    <Icon
                      size={16}
                      className={active ? "text-primary mt-0.5" : "text-muted-foreground mt-0.5"}
                      aria-hidden="true"
                    />
                    <div>
                      <div className="text-sm font-semibold text-foreground">
                        {m === "category" ? "Category" : "Last-letter chain"}
                      </div>
                      <div className="text-xs text-muted-foreground mt-0.5">
                        {m === "category"
                          ? "Match a chosen group"
                          : "Start with previous letter"}
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>
          </div>

          {pendingMode === "category" && (
            <div className="mb-5">
              <Label
                htmlFor="cat-select"
                className="block text-[10px] font-semibold uppercase tracking-wider text-muted-foreground mb-2"
              >
                Category
              </Label>
              <select
                id="cat-select"
                value={pendingCategory}
                onChange={(e) => setPendingCategory(e.target.value as GameCategoryKey)}
                className="w-full h-10 px-3 rounded-md border border-input bg-background text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              >
                {GAME_CATEGORIES.map((c) => (
                  <option key={c.key} value={c.key}>
                    {difficultyStars(c.difficulty)} · {c.label} · {c.gradeBand} ({categoryPoolSize(c.key)} elements)
                  </option>
                ))}
              </select>
              <p className="text-[11px] text-muted-foreground mt-1.5">
                {getCategory(pendingCategory).hint}
              </p>
              {previewElements.length > 0 && (
                <div className="flex flex-wrap gap-1.5 mt-2.5">
                  {previewElements.map((el) => (
                    <span
                      key={el.number}
                      className="inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-mono font-semibold bg-primary/10 text-primary border border-primary/20"
                      title={el.name}
                    >
                      {el.symbol}
                    </span>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Turn timer */}
          <div className="mb-5">
            <span className="block text-[10px] font-semibold uppercase tracking-wider text-muted-foreground mb-2">
              Turn timer
            </span>
            <div
              role="radiogroup"
              aria-label="Turn timer length"
              className="grid grid-cols-4 gap-1 p-1 rounded-md border border-border bg-background"
            >
              {TIMER_OPTIONS.map((opt) => {
                const active = turnSeconds === opt;
                const label = opt === 0 ? "Off" : `${opt}s`;
                return (
                  <button
                    key={opt}
                    type="button"
                    role="radio"
                    aria-checked={active}
                    onClick={() => setTurnSeconds(opt)}
                    className={`h-9 rounded text-sm font-medium transition-colors ${
                      active
                        ? "bg-primary text-primary-foreground shadow-sm"
                        : "text-muted-foreground hover:text-foreground"
                    }`}
                  >
                    {label}
                  </button>
                );
              })}
            </div>
          </div>

          {pendingMode === "category" && (
            <div className="mb-5">
              <span className="block text-[10px] font-semibold uppercase tracking-wider text-muted-foreground mb-2">
                In play
              </span>
              <MiniPeriodicTable states={previewStates} />
              <p className="text-[11px] text-muted-foreground mt-1.5">
                {previewElements.length} elements highlighted on the periodic table
              </p>
            </div>
          )}

          <Button onClick={beginRound} className="w-full" size="lg">
            <Play size={14} className="mr-1.5" aria-hidden="true" />
            Start round {(round?.index ?? 0) + 1}
          </Button>
        </div>
      )}

      {!betweenRounds && round && (
        <div className="rounded-xl border border-border bg-card p-5 mb-5">
          {/* Round header */}
          <div className="flex items-center justify-between gap-3 mb-3 flex-wrap">
            <div className="flex items-center gap-2 flex-wrap">
              <Badge variant="secondary" className="text-[10px]">
                Round {round.index}
              </Badge>
              {round.mode === "category" && activeCategory ? (
                <Badge variant="outline" className="text-[10px]">
                  {difficultyStars(activeCategory.difficulty)} · {activeCategory.label}
                </Badge>
              ) : (
                <Badge variant="outline" className="text-[10px]">
                  Last-letter chain
                </Badge>
              )}
              {requiredLetter && (
                <Badge className="text-[10px] font-mono">
                  Next: {requiredLetter}
                </Badge>
              )}
            </div>
            {timerEnabled && (
              <TurnTimer
                key={`turn-${round.entries.length}-${match.state.currentTurn}`}
                seconds={turnSeconds}
                paused={paused}
                onExpire={onTimerExpire}
              />
            )}
          </div>

          {/* Color-independent turn indicator */}
          <div
            className="flex items-center gap-2 mb-3 text-sm font-semibold text-foreground"
            aria-live="polite"
          >
            <ArrowRight
              size={14}
              className="text-primary flex-shrink-0"
              aria-hidden="true"
            />
            <span>
              <span className="text-muted-foreground font-normal">Now playing:</span>{" "}
              {currentPlayerName}
            </span>
          </div>

          {/* Input */}
          <form onSubmit={submitMove} className="flex gap-2 mb-3">
            <input
              ref={inputRef}
              type="text"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder={
                paused
                  ? "Paused - resume to continue"
                  : `${currentPlayerName}, name an element…`
              }
              autoComplete="off"
              autoCapitalize="off"
              spellCheck={false}
              disabled={paused}
              className="flex-1 h-10 px-3 rounded-md border border-input bg-background text-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:opacity-60 disabled:cursor-not-allowed"
              aria-label={`${currentPlayerName}, your move`}
            />
            <Button type="submit" disabled={paused}>
              Submit
            </Button>
          </form>

          {/* Action row: pause + undo */}
          <div className="flex items-center gap-2 mb-3 flex-wrap">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setPaused((p) => !p)}
              className="text-xs"
              aria-pressed={paused}
            >
              {paused ? (
                <>
                  <Play size={12} className="mr-1" aria-hidden="true" />
                  Resume
                </>
              ) : (
                <>
                  <Pause size={12} className="mr-1" aria-hidden="true" />
                  Pause timer
                </>
              )}
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={undoLast}
              disabled={round.entries.length === 0}
              className="text-xs text-muted-foreground"
              title="Teacher arbitration: undo the last answer"
            >
              <Undo2 size={12} className="mr-1" aria-hidden="true" />
              Undo last
            </Button>
          </div>

          {/* Feedback */}
          {feedback && (
            <div
              role="status"
              aria-live="polite"
              className={`flex items-center gap-2 text-sm px-3 py-2 rounded-md mb-3 ${
                feedback.kind === "success"
                  ? "bg-primary/10 text-foreground"
                  : "bg-destructive/10 text-foreground"
              }`}
            >
              {feedback.kind === "success" ? (
                <CheckCircle2 size={14} className="text-primary flex-shrink-0" aria-hidden="true" />
              ) : (
                <XCircle size={14} className="text-destructive flex-shrink-0" aria-hidden="true" />
              )}
              <span>{feedback.text}</span>
            </div>
          )}

          {/* History */}
          {round.entries.length > 0 && (
            <div>
              <div className="text-[10px] uppercase tracking-wider text-muted-foreground mb-1.5">
                History ({round.entries.length})
              </div>
              <ol className="flex flex-wrap gap-1.5">
                {round.entries.map((e, i) => (
                  <li key={i}>
                    <span
                      className="inline-flex items-center gap-1.5 px-2 py-1 rounded text-[11px] font-mono bg-muted text-foreground"
                      title={
                        e.correctedFrom
                          ? `${match.state.players[e.player].name}: ${e.display} (from "${e.correctedFrom}")`
                          : `${match.state.players[e.player].name}: ${e.display}`
                      }
                    >
                      <span className="text-muted-foreground">
                        {match.state.players[e.player].name.slice(0, 8)}
                      </span>
                      <span>·</span>
                      <span>{e.display}</span>
                    </span>
                  </li>
                ))}
              </ol>
            </div>
          )}
        </div>
      )}

      <MatchResultDialog
        state={match.state}
        open={matchOver}
        onRematch={handleRematch}
        onChangePlayers={handleChangePlayers}
        onExit={handleExit}
        detail={`Best of 3 - first to ${ROUNDS_TO_WIN} round wins.`}
      />
    </GameLayout>
  );
}

/** Build a teacherly one-line explanation for a recap mistake row. */
function explanationFor(
  failure: { reason: string; message: string },
  categoryKey?: GameCategoryKey,
): string {
  switch (failure.reason) {
    case "wrong-category":
      return categoryKey
        ? `Not a ${getCategory(categoryKey).label.toLowerCase().replace(/s$/, "")}.`
        : failure.message;
    case "wrong-letter":
      return failure.message;
    case "repeat":
      return "Already used this round.";
    case "unknown":
      return "Not a recognized element name or symbol.";
    case "empty":
      return "No answer given.";
    default:
      return failure.message;
  }
}
